/** https://www.gatsbyjs.org/docs/add-seo-component/ */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useTranslation } from "./language"

const SEO = ({ description, meta, image: img, title, pathname }) => {
  const { site } = useStaticQuery(query)
  const { lang } = useTranslation()

  const metaDescription = description || site.siteMetadata.description
  const image = img && img.src
    ? `${site.siteMetadata.siteUrl}${img.src}`
    : site.siteMetadata.image
  const canonical = pathname
    ? `${site.siteMetadata.siteUrl}${pathname}`
    : null
  const pageUrl = canonical || site.siteMetadata.siteUrl

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={canonical ? [{ rel: `canonical`, href: canonical }] : []}
      meta={[
        { name: `description`, content: metaDescription },
        { name: `keywords`, content: site.siteMetadata.keywords.join(`,`) },

        // Open Graph
        { property: `og:title`, content: title },
        { property: `og:description`, content: metaDescription },
        { property: `og:type`, content: `website` },
        { property: `og:url`, content: pageUrl },
        { property: `og:site_name`, content: site.siteMetadata.title },
        { property: `og:locale`, content: lang === "fr" ? `fr_FR` : `en_US` },

        // Twitter
        { name: `twitter:card`, content: `summary_large_image` },
        { name: `twitter:site`, content: site.siteMetadata.twitterUsername },
        { name: `twitter:creator`, content: site.siteMetadata.twitterUsername },
        { name: `twitter:title`, content: title },
        { name: `twitter:description`, content: metaDescription },
      ]
        .concat([
          { property: `og:image`, content: image },
          { name: `twitter:image`, content: image },
          ...(img ? [
            { property: `og:image:width`, content: img.width },
            { property: `og:image:height`, content: img.height },
          ] : []),
        ])
        .concat(meta)}
    />
  )
}

export default SEO

SEO.propTypes = {
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
  pathname: PropTypes.string,
}

SEO.defaultProps = {
  meta: [],
  description: ``,
}

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        title
        description
        author
        twitterUsername
        image
        keywords
        siteUrl
      }
    }
  }
`
